import { Component, ViewChild, OnDestroy, Output, EventEmitter, Optional, AfterViewInit } from '@angular/core';
import { ScheduleEmailComponent } from './schedule-email.component';
import { ScheduleEmailProps } from 'src/app/pages/group-analytics/group-analytics-email-pricing-dialog/schedule-email/schedule-email';
import { NiComponentsModule } from 'src/app/ni-components/ni-components.module';
import { MatButtonModule } from '@angular/material/button';
import { ScheduledEmailsService } from '../../manage-scheduled-emails/scheduled-emails.service';
import { SharedService } from 'src/app/layouts/shared-service';
import { EmailSchedule, EmailScheduleRequest } from 'src/app/models/scheduled-emails/email-schedule';
import { NgxUiLoaderService, NgxUiLoaderModule } from 'ngx-ui-loader';

@Component({
  selector: 'app-schedule-email-dialog-wrapper',
  template: `
    <!-- Loading Indicator -->
    <ngx-ui-loader
      [loaderId]="loaderId"
      bgsColor="rgb(100, 181, 246)"
      bgsPosition="center-center"
      bgsType="ball-spin"
      overlayColor="rgba(0, 0, 0, 0.1)"
    ></ngx-ui-loader>
      <app-schedule-email
        #scheduleForm
        [tagList]="data?.tagList || []"
        [recipientList]="recipientList || []"
        [frequency]="data?.emailSchedule?.frequency"
        [scheduleDateTime]="scheduledDateTime"
        [timezone]="data?.emailSchedule?.timeZone"
        [isEditMode]="true">
      </app-schedule-email>

      <div style="display:flex; justify-content:flex-end; gap:12px; margin-top: 16px;">
        <button mat-button (click)="onCancel()" [disabled]="isUpdating">Cancel</button>
        <button color="accent" mat-raised-button (click)="onUpdate()" [disabled]="isUpdating">
          {{ isUpdating ? 'Updating...' : 'Update' }}
        </button>
      </div>
  `,
  standalone: true,
  imports: [ScheduleEmailComponent, NiComponentsModule, MatButtonModule, NgxUiLoaderModule],
  providers: [ScheduledEmailsService, SharedService]
})
export class ScheduleEmailDialogWrapperComponent implements OnDestroy, AfterViewInit {
  @ViewChild('scheduleForm') scheduleForm!: ScheduleEmailComponent;
  public defaultDate: Date = new Date();
  public scheduledDateTime: Date | null = null;
  public recipientList: any[] = [];
  public isUpdating: boolean = false;
  
  // Data property - will be set by dialog wrapper
  private _data: any;
  get data(): any {
    return this._data;
  }
  set data(value: any) {
    this._data = value;
    if (value) {
      this.initializeScheduledDateTime();
    }
  }

  // EventEmitter for dialog communication
  @Output() cancelDialog = new EventEmitter<void>(); // Closes dialog without data
  @Output() closeDialog = new EventEmitter<any>(); // Closes dialog with data
  
  // Loader configuration
  public readonly loaderId = 'schedule-email-dialog';
  constructor(
    @Optional() private scheduledEmailsService: ScheduledEmailsService,
    @Optional() private sharedService: SharedService,
    @Optional() private ngxLoader: NgxUiLoaderService
  ) {
    // Don't initialize here - wait for data to be set
  }

  ngAfterViewInit(): void {
    // Initialize if data was set before view init
    if (this.data) {
      this.initializeScheduledDateTime();
    }
  }

  /**
   * Initialize the scheduled date and time once
   */
  private initializeScheduledDateTime(): void {
    // Prioritize scheduleDateTime over scheduleDateTimeLocal
    if (this.data?.emailSchedule?.scheduleDateTime) {
      this.scheduledDateTime = new Date(this.data.emailSchedule.scheduleDateTime);
    } else if (this.data?.emailSchedule?.scheduleDateTimeLocal) {
      this.scheduledDateTime = new Date(this.data.emailSchedule.scheduleDateTimeLocal);
    }
    
    if (this.data?.emailSchedule?.contactEmails) {
      this.recipientList = this.data.emailSchedule.contactEmails.map(email => ({ email }));
    }
  }

  onCancel() {
    // Emit cancelDialog without data - wrapper will close dialog without data
    this.cancelDialog.emit();
  }

  /**
   * Close dialog and send current form data to parent
   */
  onCloseDialog(): void {
    // Get current form values
    const vals = this.scheduleForm.getScheduleValues();
    
    // Create schedule data with current form values
    const scheduleData: ScheduleEmailProps = {
      frequency: vals.frequency,
      timezone: vals.timezone,
      scheduleDate: vals.scheduleDate,
      tagList: this.data?.tagList || [],
      recipientList: this.recipientList || []
    };
    
    // Emit closeDialog with the current form data - wrapper will close dialog with this data
    this.closeDialog.emit(scheduleData);
  }

  onUpdate() {
    if (this.isUpdating) {
      return; // Prevent multiple clicks
    }

    // Get form values
    const vals = this.scheduleForm.getScheduleValues();
    
    // If there's no oid, send the data to parent to save
    if (!this.data?.emailSchedule?.oid) {
      // Emit closeDialog with the schedule data for parent to save
      const scheduleData: ScheduleEmailProps = {
        frequency: vals.frequency,
        timezone: vals.timezone,
        scheduleDate: vals.scheduleDate,
        tagList: this.data?.tagList || [],
        recipientList: this.recipientList || []
      };
      this.closeDialog.emit(scheduleData);
      return;
    }

    // If there's an oid, update via service
    this.isUpdating = true;
    // Start loader during update operation
    if (this.ngxLoader) {
      this.ngxLoader.startLoader(this.loaderId);
    }

    const request: EmailScheduleRequest = {
      frequency: vals.frequency,
      timeZone: vals.timezone,
      scheduleDateTime: vals.scheduleDate,
      groupId: this.sharedService?.currentUser?.groupId,
      customerIds: [this.data?.emailSchedule?.customerId ?? 0],
      createdBy: this.sharedService?.currentUser?.oid
    };

    if (this.scheduledEmailsService) {
      this.scheduledEmailsService.updateScheduleEmail(this.data.emailSchedule.oid, request)
        .subscribe({
          next: (updated: EmailSchedule) => {
            this.isUpdating = false;
            if (this.ngxLoader) {
              this.ngxLoader.stopLoader(this.loaderId);
            }
            // Emit closeDialog with the updated data - wrapper will close dialog with this data
            this.closeDialog.emit(updated);
          },
          error: (error) => {
            this.isUpdating = false;
            if (this.ngxLoader) {
              this.ngxLoader.stopLoader(this.loaderId);
            }
            // Emit cancelDialog without data on error
            this.cancelDialog.emit();
          }
        });
    }
  }


  ngOnDestroy(): void {
    // Clean up any resources
    this.scheduledDateTime = null;
  }
}


